import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import Select from 'react-select';
import Dropzone from 'react-dropzone';
import S3Upload from 'react-s3-uploader/s3upload.js';
import AssessmentApi from '../../../api/AssessmentApi';
import * as assessmentsActions from '../../../redux/actions/assessmentsActions';
import history from '../../../utils/history';
import { convertDate } from '../../../utils/utils';

import Modal from '../../common/Modal';
import UploadProgressBar from './UploadProgressBar';
import UpDownArrowIcon from '../../icons/UpDownArrowIcon';
import FileIcon from '../../icons/FileIcon';
import SectionCheckboxIcon from '../../icons/SectionCheckboxIcon';
import InputErrors from '../../common/InputErrors';

class UploadAssessmentModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      file: '',
      section: '',
      date: '',
      sectionError: false,
      showSuccess: false,
      fileError: false,
      fileTypeError: false,
      fileSizeError: false,
      percentage: 0,
      maxFileSize: 10000000,
      rejectedSize: 0,
      serverErrors: [],
    };
    this.dropzoneRef = React.createRef();
  }

  handleChange = (e) => {
    const { value, name } = e.target;
    this.setState({ [name]: value });
  }

  preprocess = (file, next) => {
    file.section = this.state.section;
    file.date = this.state.date;
    return next(file);
  }

  handleSelectChange = ({ value }) => {
    this.setState({ section: value });
  }

  onDropAccepted = (files) => {
    this.dropzoneRef.current.node.classList.remove('dragging');
    console.log(files[0]);
    this.setState({
      fileSizeError: false,
      fileTypeError: false,
      serverErrors: [],
      file: files[0], // Dropzone returns an array of files, but we're only working with one at a time
    });
  }

  onDrop = (acceptedFiles, rejectedFiles) => {
    if (rejectedFiles.length > 0) {
      let rejected = rejectedFiles[0];
      if (rejected.size > this.state.maxFileSize) {
        this.setState({
          fileSizeError: true,
          rejectedSize: (rejected.size / 1000000).toFixed(2),
        });
      }
      if (rejected.type !== 'application/pdf') {
        this.setState({ fileTypeError: true });
      }
    }
  }

  onDropRejected = () => {
    this.dropzoneRef.current.node.classList.remove('dragging');
  }

  onDragEnter = () => {
    this.dropzoneRef.current.node.classList.add('dragging');
  }

  onDragLeave = () => {
    this.dropzoneRef.current.node.classList.remove('dragging');
  }

  resetModal = () => {
    this.setState({
      file: '',
      section: '',
      date: '',
      showSuccess: false,
      sectionError: false,
      fileError: false,
      fileTypeError: false,
      percentage: 0,
      serverErrors: [],
    });
  }

  closeModalAndNavigate = () => {
    const { toggleUploadAssessmentModal } = this.props;

    this.resetModal();
    toggleUploadAssessmentModal();
    history.push('/assessments');
  }

  onProgress = (percentage) => {
    console.log(percentage);
    this.setState({ percentage });
    if (percentage === 100) {
      this.setState({ showSuccess: true });
    }
  }

  onSignedError = (error) => {
    this.setState({
      serverErrors: error.response
        ? error.response.data
        : ['There was a problem connecting to the server.'],
    });
  }

  onUploadError = (error) => {
    // TO DO: udpate this to match the structure of an S3 upload error
    console.log(error);
    this.setState({
      serverErrors: error.response
        ? error.response.data
        : ['There was a problem connecting to the server.'],
    });
  }

  getSignedUrl = (assessment, callback) => {
    AssessmentApi.getSignedUrl(assessment, callback, this.onSignedError);
  }

  submit = (e) => {
    e.preventDefault();
    this.setState({
      sectionError: false,
      fileError: false,
      fileTypeError: false,
      serverErrors: [],
    });
    if (this.state.file === '') {
      this.setState({ fileError: true });
    } else if (this.state.section === '') {
      this.setState({ sectionError: true });
    } else {
      this.setState({ sectionError: '' });
      new S3Upload({
        files: [this.state.file],
        getSignedUrl: this.getSignedUrl,
        preprocess: this.preprocess,
        onProgress: this.onProgress,
        onError: this.onUploadError,
        onFinishS3Put: this.props.assessmentsActions.getAssessments,
        uploadRequestHeaders: { 'acl': 'private' },
        contentDisposition: 'auto',
      });
      document.querySelector('.ReactModal__Content').focus(); // keep focus inside the modal
    }
  }

  render() {
    const { fileError, fileTypeError, sectionError, fileSizeError, rejectedSize, maxFileSize, showSuccess, file, section, date, percentage, serverErrors } = this.state;
    // Aggregate error messages
    let errors = serverErrors;
    if (fileError) {
      errors = [...errors, 'Please choose a file.'];
    }
    if (fileTypeError) {
      errors = [...errors, 'Uploaded assessments must be in PDF format.'];
    }
    if (sectionError) {
      errors = [...errors, 'Please choose a module.'];
    }
    if (fileSizeError) {
      errors = [...errors, `Upload is ${rejectedSize} megabytes; must be smaller than ${maxFileSize / 1000000} megabytes`];
    }

    return (
      <Modal
        toggleModal={this.props.toggleUploadAssessmentModal}
        visible={this.props.visible}
        centered
      >
        {showSuccess
          ? (
            <Fragment>
              <div className="text">
                <h2>
                  Your Document Has Been Uploaded&nbsp;Successfully
                </h2>
                <div className="modal__file">
                  <FileIcon />
                  <div>
                    {file.name}
                    <br />
                    added to:
                  </div>
                </div>
                <div className="assessments-list assessments-list--upload-modal">
                  <div className="assessments-list__card assessments-list__card--upload-modal">
                    <div className="assessments-list__checkbox assessments-list__col-1">
                      <SectionCheckboxIcon theme={this.props.sections[section].theme} />
                    </div>
                    <div className={'assessments-list__section assessments-list__col-2 color--' + this.props.sections[section].theme}>
                      {this.props.sections[section].fullTitle}
                    </div>
                    <div className="assessments-list__date assessments-list__col-3">
                      Completed:
                      {' '}
                      {date ? convertDate(date) : convertDate(Date.now())}
                    </div>
                    <div className="assessments-list__name assessments-list__col-4">
                      {this.props.user.firstName + ' ' + this.props.user.lastName}
                    </div>
                  </div>
                </div>
              </div>
              <button
                type="button"
                className="button"
                onClick={this.closeModalAndNavigate}
              >
                See Assessments
              </button>
            </Fragment>
          ) : (
            <Fragment>
              <div className="text">
                <h2>
                  Upload a Document
                </h2>
                <p className="modal__extra-padding-paragraph">
                  Select a PDF <span className="hide-on-mobile">from your computer or drag and drop the file below</span> to add to your assessments.
                </p>
              </div>
              <form className="form form--modal">
                {errors.length !== 0 && <InputErrors errors={errors} />}
                <Dropzone
                  maxSize={maxFileSize}
                  onDropAccepted={this.onDropAccepted}
                  multiple={false}
                  className="form__file-label"
                  disablePreview
                  onDrop={this.onDrop}
                  onDragStart={this.onDragStart}
                  onDropRejected={this.onDropRejected}
                  onDragEnter={this.onDragEnter}
                  onDragLeave={this.onDragLeave}
                  accept="application/pdf"
                  ref={this.dropzoneRef}
                >
                  <span className="hide-on-mobile">Drag and drop your file here or</span>
                  <div className="flat-link">
                    Browse Files
                  </div>
                </Dropzone>
                {file && (
                  <div className="form__file-name">
                    {file.name}
                    <div className="form__file-size">
                      {percentage > 0 && (
                        <Fragment>
                          {Math.round(file.size * percentage / 100)}
                          {' of '}
                        </Fragment>
                      )}
                      {`${file.size} bytes`}
                    </div>
                  </div>
                )}

                {file && percentage > 0 && <UploadProgressBar progress={percentage} />}
                <label
                  htmlFor="upload-modal-section"
                  className="form__text-label"
                  id="upload-modal-section-label"
                  required
                >
                  Select module type
                </label>
                <Select
                  id="upload-modal-section"
                  onChange={this.handleSelectChange}
                  value={section}
                  className="form__select"
                  name="section"
                  searchable={false}
                  clearable={false}
                  required
                  menuPlacement="auto"
                  menuShouldScrollIntoView
                  arrowRenderer={({ isOpen }) => (
                    <UpDownArrowIcon direction={isOpen ? 'asc' : 'desc'} />
                  )}
                  options={Object.entries(this.props.sections).map(([id, section]) => ({
                    value: id,
                    label: section.title,
                  }))}
                />
                {/*
                  <select
                    aria-labelledby="upload-modal-section-label"
                    id="upload-modal-section"
                    name="section"
                    value={this.state.section}
                    onChange={this.handleChange}
                  >
                    <option disabled value="" />
                    {Object.entries(this.props.sections).map(([id, section]) => (
                      <option
                        key={id}
                        value={id}
                      >
                        {section.title}
                      </option>
                    ))
                  </select>
                */}
                <label htmlFor="upload-modal-date" className="form__text-label">
                  Enter date of completion (leave blank to use today’s date)
                </label>
                <input
                  id="upload-modal-date"
                  name="date"
                  value={date}
                  onChange={this.handleChange}
                  type="date"
                />
                <div className="form__submit-button-wrapper">
                  <button
                    type="submit"
                    className="button"
                    onClick={this.submit}
                  >
                    Submit
                  </button>
                </div>
              </form>
            </Fragment>
          )}
      </Modal>
    )
  }
}

function mapStateToProps(state) {
  return {
    sections: state.sections || {},
    user: state.users.roster[state.users.currentUserId],
  };
}

function mapDispatchToProps(dispatch) {
  return { assessmentsActions: bindActionCreators(assessmentsActions, dispatch) };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(UploadAssessmentModal);



// WEBPACK FOOTER //
// ./src/components/features/dashboard/UploadAssessmentModal.js